/**
 * Search API Types
 * 
 * TypeScript types matching the backend API schemas.
 * These mirror the Pydantic models from the backend.
 */

// -----------------------------------------------------------------------------
// Enums (kept as reference/fallback values)
// The UI primarily uses dynamic facets from the backend, but these provide
// type safety and fallback values when facets are loading.
// -----------------------------------------------------------------------------

export const Source = {
  ZENON_DOCS: 'zenon_docs',
  GITHUB: 'github',
  COPADATA_DOCKERHUB: 'copadata_dockerhub',
  COPADATA_YOUTUBE: 'copadata_youtube',
  MOCK: 'mock',  // For testing purposes
} as const;

export type Source = typeof Source[keyof typeof Source];

export const SourceType = {
  // Documentation
  DOC_PAGE: 'doc_page',
  
  // GitHub
  GITHUB_ISSUE: 'github_issue',
  GITHUB_PULL_REQUEST: 'github_pull_request',
  GITHUB_COMMIT: 'github_commit',
  GITHUB_DISCUSSION: 'github_discussion',
  GITHUB_RELEASE: 'github_release',
  GITHUB_FILE: 'github_file',
  GITHUB_WIKI: 'github_wiki',
  GITHUB_COMMENT: 'github_comment',
  
  // Docker
  DOCKER_IMAGE: 'docker_image',
  
  // Multimedia
  VIDEO: 'video',
} as const;

export type SourceType = typeof SourceType[keyof typeof SourceType];

// -----------------------------------------------------------------------------
// Facets Types (Dynamic filtering)
// -----------------------------------------------------------------------------

export interface FacetValue {
  value: string;
  display_name: string;
  category?: string;
  /**
   * When set, this value is a synthetic alias for another value of the same
   * field (`latest` -> `1500`). No document carries the alias itself, so any
   * document-derived data (counts, co-occurrence) must be read from the
   * target value instead.
   */
  alias_for?: string | null;
  document_count: number;
  priority: number;
}

export interface FacetField {
  field_name: string;
  display_name: string;
  multi_select: boolean;
  values: FacetValue[];
}

/**
 * One entry of the static facet co-occurrence map.
 *
 * Each entry describes which value-pairs have actually been observed
 * together on a single document across `field_a` and `field_b`.
 * Field order is canonical (alphabetical), so `field_a < field_b`.
 */
export interface FacetCoOccurrencePairs {
  field_a: string;
  field_b: string;
  /** Tuples of [value_of_field_a, value_of_field_b]. */
  pairs: Array<[string, string]>;
}

/**
 * Static pairwise co-occurrence map across facet fields.
 *
 * Surfaced by `/api/facets` so the UI can grey out filter values whose
 * selection would yield zero results given the currently active filters.
 */
export interface FacetCoOccurrence {
  pairs: FacetCoOccurrencePairs[];
}

export interface FacetsResponse {
  facets: FacetField[];
  co_occurrence?: FacetCoOccurrence | null;
  cached_at?: string;
  expires_at?: string;
}

// -----------------------------------------------------------------------------
// Request Types
// -----------------------------------------------------------------------------

/**
 * Search filters - all filter fields accept string arrays.
 * The available values are loaded dynamically from the /facets endpoint.
 * 
 * Supports both well-known fields (source, source_type, etc.) and
 * dynamic fields via index signature for extensibility.
 */
export interface SearchFilters {
  source?: string[];
  source_type?: string[];
  language?: string[];
  zenon_version?: string[];
  tags?: string[];
  created_after?: string;
  created_before?: string;
  updated_after?: string;
  updated_before?: string;
  /** Allow additional dynamic filter fields */
  [key: string]: string[] | string | undefined;
}

export interface SearchRequest {
  query: string;
  filters?: SearchFilters;
  size?: number;
  from?: number;
  use_semantic?: boolean;
  user_language?: string;
}

// -----------------------------------------------------------------------------
// Response Types
// -----------------------------------------------------------------------------

export interface PersonResponse {
  name: string;
  type?: string;
  role?: string;
  email?: string;
  id?: string;
}

export interface PopularityResponse {
  views?: number;
  likes?: number;
  stars?: number;
  pulls?: number;
  comments?: number;
}

export interface SearchResultItem {
  id: string;
  source: string;
  source_type: string;
  parent_document_id?: string;
  chunk_index?: number;
  
  title?: string;
  content_full_md?: string;
  chunk_md?: string;
  summary?: string;
  url?: string;
  thumbnail_url?: string;
  
  language?: string;
  zenon_version?: string;
  document_version?: string;
  tags?: string[];
  breadcrumb?: string[];
  keywords?: string[];
  
  created_at?: string;
  updated_at?: string;
  published_at?: string;
  
  persons?: PersonResponse[];
  popularity?: PopularityResponse;
  popularity_score?: number;
  is_multilingual?: boolean;
  
  score: number;
  highlights?: Record<string, string[]>;
  matching_chunks?: Array<{
    chunk_index?: number;
    chunk_md?: string;
    highlight?: string[];
  }>;
  
  source_fields?: Record<string, unknown>;
}

export interface SearchResponse {
  results: SearchResultItem[];
  total: number;
  query: string;
  size: number;
  from: number;
  took_ms?: number;
  /**
   * Whether the caller's ACL principals could be fully resolved on the
   * backend.
   * - "ok": full results
   * - "limited": some legitimately-visible documents may be missing
   *   (e.g. Zoho account membership lookup failed). The UI shows a subtle
   *   notice in that case.
   */
  acl_status?: 'ok' | 'limited';
}

// -----------------------------------------------------------------------------
// Document Types
// -----------------------------------------------------------------------------

export interface DocumentResponse {
  id: string;
  source: string;
  source_type: string;
  native_id?: string;
  url?: string;
  title?: string;
  content_full_md?: string;
  summary?: string;
  language?: string;
  created_at?: string;
  updated_at?: string;
  source_fields?: Record<string, unknown>;
}

// -----------------------------------------------------------------------------
// Suggest (type-ahead) Types
// -----------------------------------------------------------------------------

export interface SuggestionItem {
  text: string;
  document_id?: string;
  source?: string;
  source_type?: string;
  url?: string;
}

export interface SuggestRequest {
  q: string;
  size?: number;
  user_language?: string;
  audience?: string;
}

export interface SuggestResponse {
  suggestions: SuggestionItem[];
  query: string;
  took_ms?: number;
}

// -----------------------------------------------------------------------------
// Health Check Types
// -----------------------------------------------------------------------------

export interface HealthResponse {
  status: string;
  elasticsearch: string;
  timestamp: string;
}

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

import i18n from '../i18n';

/**
 * Get display label for a Source enum value.
 *
 * Looks up the translation under `search:facets.values.source.<value>`.
 * Falls back to the raw value if no translation is registered.
 */
export function getSourceLabel(source: Source | string): string {
  const key = `facets.values.source.${source}`;
  const translated = i18n.t(key, { ns: 'search', defaultValue: '' });
  return translated || source;
}

/**
 * Get display label for a SourceType enum value.
 *
 * Looks up the translation under `search:facets.values.source_type.<value>`.
 * Falls back to the raw value if no translation is registered.
 */
export function getSourceTypeLabel(sourceType: SourceType | string): string {
  const key = `facets.values.source_type.${sourceType}`;
  const translated = i18n.t(key, { ns: 'search', defaultValue: '' });
  return translated || sourceType;
}

/**
 * Get an icon name for a source type
 */
export function getSourceIcon(source: Source | string): string {
  const icons: Record<string, string> = {
    [Source.ZENON_DOCS]: 'description',
    [Source.GITHUB]: 'code',
    [Source.COPADATA_DOCKERHUB]: 'inventory_2',
    [Source.COPADATA_YOUTUBE]: 'play_circle',
    [Source.MOCK]: 'science',  // Lab/testing icon
  };
  return icons[source] || 'article';
}
